'use client';

import { useEffect, useRef, useState } from 'react';
import { PhoneIcon, PhoneXMarkIcon, MicrophoneIcon } from '@heroicons/react/24/solid';
import { clsx } from 'clsx';
import { useTwilioDevice } from '@hooks/useTwilioDevice';
import type { RecruiterCallConnectParams } from '@type/recruiter-call';

interface RecruiterCallSoftphoneModalProps {
  open: boolean;
  candidateName: string;
  candidateUserId: number;
  jobSlug?: string | null;
  /** Cost in credits that will be charged at completion. 0 = free. */
  priceCredits?: number;
  onClose: () => void;
}

const formatDuration = (seconds: number) => {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

/**
 * Minimal softphone modal. Opens when the recruiter clicks "Call candidate",
 * mints a Twilio Voice token, initiates the call, and shows status +
 * duration + mute + hang-up. Recording + transcript + AI analysis are
 * handled server-side asynchronously — this modal only owns the *live*
 * call. Post-call artifacts render in a separate `RecruiterCallResult`
 * component on the candidate profile.
 */
export const RecruiterCallSoftphoneModal = ({
  open,
  candidateName,
  candidateUserId,
  jobSlug,
  priceCredits = 0,
  onClose,
}: RecruiterCallSoftphoneModalProps) => {
  const {
    state, error, isMuted, callDuration, connect, disconnect, toggleMute,
  } = useTwilioDevice();
  const [confirmed, setConfirmed] = useState(false);
  // Tracks whether this modal session ever reached an active-call state.
  // Used to auto-close the modal when the *remote* side hangs up (Twilio
  // fires `disconnect` → hook flips state back to `ready`, and without
  // this flag we can't tell "ended" apart from "never started").
  const wasActiveRef = useRef(false);

  useEffect(() => {
    if (state === 'connecting' || state === 'in-call') {
      wasActiveRef.current = true;
    }
  }, [state]);

  useEffect(() => {
    if (!open || !confirmed) return;
    if (state !== 'idle' && state !== 'ready' && state !== 'error') return;
    if (wasActiveRef.current) return; // don't re-dial after the call ended
    const params: RecruiterCallConnectParams = {
      candidateUserId: String(candidateUserId),
      jobSlug: jobSlug ?? '',
    };
    void connect(params);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, confirmed]);

  // Auto-close ~1.5s after Twilio reports the call ended — covers the
  // "candidate hung up first" case where the recruiter has no reason to
  // keep the modal open. Doesn't override the recruiter's own hang-up
  // (that path already calls onClose synchronously).
  useEffect(() => {
    if (!open) return undefined;
    if (!wasActiveRef.current) return undefined;
    if (state !== 'ready' && state !== 'error') return undefined;
    const t = setTimeout(() => onClose(), 1500);
    return () => clearTimeout(t);
  }, [state, open, onClose]);

  if (!open) return null;

  const statusLabel = (() => {
    switch (state) {
      case 'registering': return 'Se pregătește microfonul…';
      case 'connecting': return 'Se sună…';
      case 'in-call': return `În apel — ${formatDuration(callDuration)}`;
      case 'ready': return wasActiveRef.current ? 'Apel încheiat' : 'Gata';
      case 'error': return 'Eroare';
      default: return '';
    }
  })();

  const handleHangUp = () => {
    disconnect();
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="recruiter-call-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      <div className="w-[92%] max-w-sm rounded-3xl bg-surface p-6 shadow-xl">
        <div className="mb-4 text-center">
          <div id="recruiter-call-modal-title" className="text-lg font-semibold text-ink">
            {candidateName}
          </div>
          {confirmed ? (
            <div className="mt-1 text-sm text-ink-medium">{statusLabel}</div>
          ) : (
            <div className="mt-1 text-sm text-ink-medium">
              {priceCredits > 0
                ? `Costul apelului: ${priceCredits} credite`
                : 'Apel gratuit'}
            </div>
          )}
        </div>

        {error && (
          <div className="mb-3 rounded-2xl bg-error/10 p-3 text-sm text-error">
            {error}
          </div>
        )}

        {!confirmed ? (
          <div className="mt-4 flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex h-11 min-w-24 items-center justify-center rounded-2xl border border-input bg-surface-50 px-4 text-sm font-medium text-ink-medium hover:bg-surface-100"
            >
              Anulează
            </button>
            <button
              type="button"
              onClick={() => setConfirmed(true)}
              className="flex h-11 min-w-32 items-center justify-center gap-2 rounded-2xl bg-secondary px-4 text-sm font-semibold text-white shadow hover:bg-secondary/90"
            >
              <PhoneIcon className="size-4" />
              Sună acum
            </button>
          </div>
        ) : (
          <div className="mt-4 flex items-center justify-center gap-6">
            <button
              type="button"
              onClick={toggleMute}
              disabled={state !== 'in-call'}
              aria-pressed={isMuted}
              aria-label={isMuted ? 'Unmute' : 'Mute'}
              className={clsx(
                'flex size-14 items-center justify-center rounded-full border transition',
                isMuted
                  ? 'border-warning bg-warning/20 text-warning'
                  : 'border-input bg-surface-50 text-ink-medium hover:bg-surface-100',
                state !== 'in-call' && 'cursor-not-allowed opacity-50',
              )}
            >
              <MicrophoneIcon className="size-6" />
            </button>

            <button
              type="button"
              onClick={handleHangUp}
              aria-label="End call"
              className="flex size-16 items-center justify-center rounded-full bg-error text-white shadow-lg transition hover:bg-error/90"
            >
              <PhoneXMarkIcon className="size-7" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
